import types from "../types";
import { CREATIVE_JOB_RTDB_SOURCE } from "../utils/creativeJobsNotifications";

const initialState = {
  integrationNotFound: {
    open: false,
    items: [],
  },
  pendingIntegrationFormSync: null,
  notifications: [],
  previousNotifications: [],
  notificationsPanelIsExpanded: false,
  notificationsPanelIsClosed: false,
};

export const alertsReducer = (state = initialState, action) => {
  let itemsFiltered;
  switch (action.type) {
    case types.UI_SET_NOTIFICATIONS_PREVIOUS_NOTIFICATIONS:
      return {
        ...state,
        previousNotifications: action.payload,
      };
    case types.UI_SET_NOTIFICATIONS_PANEL_IS_EXPANDED:
      return {
        ...state,
        notificationsPanelIsExpanded: action.payload,
      };
    case types.UI_SET_NOTIFICATIONS_PANEL_IS_CLOSED:
      return {
        ...state,
        notificationsPanelIsClosed: action.payload,
      };
    case types.ALERTS_SET_INTEGRATIONS_NOT_FOUND: {
      // Skip if the modal is already active to avoid concurrent payloads overwriting.
      if (
        state.integrationNotFound.open ||
        state.integrationNotFound.items.length > 0
      ) {
        return state;
      }
      return {
        ...state,
        integrationNotFound: {
          ...state.integrationNotFound,
          items: Array.isArray(action.payload) ? action.payload : [],
        },
      };
    }
    case types.ALERTS_SHOW_MODAL_INTEGRATION_NOT_FOUND: {
      return {
        ...state,
        integrationNotFound: {
          ...state.integrationNotFound,
          open: action.payload === true,
        },
      };
    }
    case types.ALERTS_RESET_INTEGRATION_NOT_FOUND: {
      return {
        ...state,
        integrationNotFound: {
          open: false,
          items: [],
        },
      };
    }
    case types.ALERTS_SET_PENDING_INTEGRATION_FORM_SYNC: {
      const payload = action.payload;
      const incoming = Array.isArray(payload?.items) ? payload.items : [];
      const prev = state.pendingIntegrationFormSync?.items || [];
      const merged = [...prev, ...incoming];
      return {
        ...state,
        pendingIntegrationFormSync:
          merged.length > 0 ? { items: merged } : null,
      };
    }
    case types.ALERTS_CONSUME_PENDING_INTEGRATION_FORM_SYNC_FOR_CAMPAIGN: {
      const campaignID = action.payload;
      const current = state.pendingIntegrationFormSync;
      if (!current?.items?.length || !campaignID) {
        return state;
      }
      const remaining = current.items.filter(
        (item) => item.campaignID !== campaignID
      );
      return {
        ...state,
        pendingIntegrationFormSync:
          remaining.length > 0 ? { items: remaining } : null,
      };
    }
    case types.GET_FACEBOOK_INTEGRATION:
    case types.GET_GOOGLE_ADS_INTEGRATION:
    case types.GET_TIKTOK_BUSINESS_INTEGRATION:
    case types.GET_GOOGLE_DRIVE_INTEGRATION:
    case types.GET_TIKTOK_INTEGRATION:
    case types.GET_CANVA_INTEGRATION:
    case types.GET_YOUTUBE_INTEGRATION: {
      const integrationID =
        action?.payload?.integrationID || action?.payload?.id;
      itemsFiltered = (state.integrationNotFound.items || []).filter(
        (item) => item.integrationID !== integrationID
      );
      return {
        ...state,
        integrationNotFound: {
          ...state.integrationNotFound,
          items: itemsFiltered,
          open: state.integrationNotFound.open && itemsFiltered.length > 0,
        },
      };
    }

    // New notification cases (migrated from adsCampaignsReducer)
    case types.ALERTS_ADD_NOTIFICATION: {
      // Check if a notification with the same id already exists
      const existingNotificationIndex = state.notifications.findIndex(
        (notification) => notification.id === action.payload.id
      );

      if (existingNotificationIndex !== -1) {
        // If it exists, update the existing notification
        const updatedNotifications = [...state.notifications];
        updatedNotifications[existingNotificationIndex] = {
          ...updatedNotifications[existingNotificationIndex],
          ...action.payload,
          timestamp: Date.now(),
        };
        return {
          ...state,
          notifications: updatedNotifications,
        };
      } else {
        // If it doesn't exist, add new notification
        return {
          ...state,
          notifications: [
            ...state.notifications,
            {
              ...action.payload,
              timestamp: action.payload.timestamp ?? Date.now(),
            },
          ],
        };
      }
    }

    case types.ALERTS_UPDATE_NOTIFICATION: {
      return {
        ...state,
        notifications: state.notifications.map((notification) =>
          notification.id === action.payload.id
            ? { ...notification, ...action.payload.updates }
            : notification
        ),
      };
    }

    case types.ALERTS_REMOVE_NOTIFICATION: {
      return {
        ...state,
        notifications: state.notifications.filter(
          (notification) => notification.id !== action.payload.id
        ),
      };
    }

    case types.ALERTS_CLEAR_ALL_NOTIFICATIONS: {
      return {
        ...state,
        notifications: [],
      };
    }

    case types.ALERTS_SYNC_CREATIVE_JOB_NOTIFICATIONS: {
      const creativeJobNotifications = Array.isArray(action.payload)
        ? action.payload
        : [];
      const existingById = new Map(
        state.notifications.map((notification) => [
          notification.id,
          notification,
        ])
      );
      const nonCreativeJobNotifications = state.notifications.filter(
        (notification) => notification.source !== CREATIVE_JOB_RTDB_SOURCE
      );
      const mergedCreativeNotifications = creativeJobNotifications.map(
        (notification) => {
          const existing = existingById.get(notification.id);
          return {
            ...notification,
            timestamp:
              existing?.timestamp ?? notification.timestamp ?? Date.now(),
          };
        }
      );

      return {
        ...state,
        notifications: [
          ...nonCreativeJobNotifications,
          ...mergedCreativeNotifications,
        ],
      };
    }

    case types.ALERTS_PROMOTE_CREATIVE_NOTIFICATION: {
      const { fromId, jobID } = action.payload || {};
      if (!fromId || !jobID || fromId === jobID) {
        return state;
      }

      const notificationsWithoutDuplicateJob = state.notifications.filter(
        (notification) =>
          notification.id !== jobID || notification.id === fromId
      );

      return {
        ...state,
        notifications: notificationsWithoutDuplicateJob.map((notification) =>
          notification.id === fromId
            ? {
                ...notification,
                id: jobID,
                jobID,
                source: CREATIVE_JOB_RTDB_SOURCE,
              }
            : notification
        ),
      };
    }

    case types.GLOBAL_RESET_STATE:
      return initialState;
    case types.GLOBAL_RESET_ADVERTISER_DATA:
      return initialState;

    default:
      return state;
  }
};
